<div class="py-24 sm:py-32">
  <!-- Hero Section -->
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
    <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4" @fadeIn>
      {{ "pricing.title" | translate }}
    </h1>
    <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-10" @fadeIn>
      {{ "pricing.subtitle" | translate }}
    </p>

    <!-- Billing Toggle -->
    <div class="flex items-center justify-center mb-12" @fadeIn>
      <span
        [class.text-gray-900]="selectedBilling === 'monthly'"
        [class.text-gray-500]="selectedBilling !== 'monthly'"
        class="text-sm font-medium mr-3 transition-colors duration-200"
        >{{ "pricing.billing.monthly" | translate }}</span
      >
      <button
        (click)="toggleBilling()"
        class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
        [class.bg-indigo-600]="selectedBilling === 'yearly'"
        [class.bg-gray-200]="selectedBilling === 'monthly'"
      >
        <span class="sr-only">Toggle billing frequency</span>
        <span
          [class.translate-x-5]="selectedBilling === 'yearly'"
          [class.translate-x-0]="selectedBilling === 'monthly'"
          class="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
        >
        </span>
      </button>
      <span
        [class.text-gray-900]="selectedBilling === 'yearly'"
        [class.text-gray-500]="selectedBilling !== 'yearly'"
        class="text-sm font-medium ml-3 transition-colors duration-200"
      >
        {{ "pricing.billing.yearly" | translate }}
        <span class="text-green-600 font-semibold">{{
          "pricing.billing.savings" | translate
        }}</span>
      </span>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-center items-center min-h-[400px]">
      <div
        class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"
      ></div>
    </div>
  </div>

  <!-- Error State -->
  <div
    *ngIf="error"
    class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center"
  >
    <div class="bg-red-50 p-6 rounded-lg">
      <p class="text-red-600">
        {{ "pricing.error.message" | translate }}
      </p>
      <button
        (click)="fetchPackages()"
        class="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        {{ "pricing.error.retry" | translate }}
      </button>
    </div>
  </div>

  <!-- Pricing Grid -->
  <div *ngIf="!loading" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Package Card (repeated for each package) -->
      <div
        *ngFor="let pkg of packages; let i = index"
        class="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:translate-y-[-8px]"
        @staggered
      >
        <!-- Package Header -->
        <div
          class="p-8"
          [ngClass]="{
            'bg-gradient-to-br from-blue-50 to-indigo-100': i === 0,
            'bg-gradient-to-br from-indigo-50 to-purple-100': i === 1,
            'bg-gradient-to-br from-purple-50 to-pink-100': i === 2
          }"
        >
          <h3 class="text-2xl font-bold" [innerHTML]="getPackageName(pkg)"></h3>
          <div class="mt-4 flex items-baseline">
            <span class="text-xl font-semibold text-gray-500 mr-1">{{ "pricing.price.prefix" | translate }}</span>
            <span class="text-4xl font-extrabold tracking-tight">{{
              getDisplayPrice(pkg) | currency : "OMR " : "symbol" : "1.0-0"
            }}</span>
            <span class="ml-1 text-xl font-semibold text-gray-500">{{ "pricing.price.suffix" | translate }}</span>
          </div>
          <p class="mt-2 text-sm text-gray-500" *ngIf="!pkg.isVATIncluded">
            {{ "pricing.price.vat" | translate }}
          </p>

          <button
            class="mt-6 w-full py-3 px-4 cursor-pointer rounded-lg font-medium text-white transition-all duration-200 hover:shadow-md"
            [ngClass]="{
              'bg-blue-600 hover:bg-blue-700': i === 0,
              'bg-indigo-600 hover:bg-indigo-700': i === 1,
              'bg-purple-600 hover:bg-purple-700': i === 2
            }"
            [routerLink]="['/contact-us']"
          >
            {{ "pricing.cta.get_started" | translate }}
          </button>
        </div>

        <!-- Features List -->
        <div class="p-8">
          <h4 class="text-lg font-semibold mb-4">{{ "pricing.features.title" | translate }}</h4>
          <ul class="space-y-3">
            <li *ngFor="let feature of pkg.webPackagePriceDetails" class="flex">
              <span
                *ngIf="feature.isIncluded"
                class="flex-shrink-0 h-5 w-5 text-green-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span
                *ngIf="!feature.isIncluded"
                class="flex-shrink-0 h-5 w-5 text-gray-400"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <span class="ml-3 text-gray-700" [innerHTML]="getFeatureName(feature)"></span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- FAQ Section -->
  <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-20">
    <h2 class="text-3xl font-bold text-center mb-12">
      {{ "pricing.faq.title" | translate }}
    </h2>

    <div class="space-y-8">
      <div
        *ngFor="let faq of 'pricing.faq.items' | translate"
        class="bg-white rounded-xl shadow-md p-6"
      >
        <h3 class="text-lg font-semibold text-gray-900">{{ faq.question }}</h3>
        <p class="mt-2 text-gray-600">{{ faq.answer }}</p>
      </div>
    </div>
  </div>
</div>
